import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useApprovals, useProfileChanges, useWorkspaceQueue } from '@/api/hooks';
import type { Me } from '@/api/session';
import * as RD from '@radix-ui/react-dialog';
import { Avatar, Count, IconButton, Kicker } from '@/ui/primitives';
import { entityTitle, useRecordKinds } from '@/records/Flex';
import { useDepartmentNames, type DepartmentNames } from './departments';
import { useFrameMaybe } from './frame';
import { ScopeSwitcher } from '@/ui/menu';
import { INSIGHTS } from '@/insight/Insight';
import { navFor, type NavGroup, type Scope } from './nav';
import { useScope } from './scope';

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-tap shrink-0 items-center gap-2.5 px-2.5 text-dense no-underline ${
    isActive ? 'bg-ink text-page hover:text-page' : 'text-ink hover:bg-sunken hover:text-ink'
  }`;

function Group({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="flex shrink-0 flex-col gap-0.5">
      {label && (
        <span aria-hidden className="block px-2.5 pb-1.5 pt-4">
          <Kicker size="rail">{label}</Kicker>
        </span>
      )}
      {children}
    </div>
  );
}

/**
 * The doors for this person in the department in view (`navFor`), from
 * what the platform says of it: its name, whether it only reads, and the
 * record kinds live in it. A pipeline is a department's own deals; one that
 * keeps none is not offered a view of nothing (proposal 0008).
 */
export function useNav(me: Me, directory: DepartmentNames): NavGroup[] {
  const { scope } = useScope(me);
  return useRails(me, directory)(scope);
}

/**
 * Any of this person's scopes' rails, drawn as the rail would draw it. The
 * record kinds are known for the session's department only; another
 * department's rail is drawn without them, which is every door but its
 * record kinds — enough to say where a page belongs (`ownersOf`).
 */
export function useRails(me: Me, directory: DepartmentNames): (scope: Scope) => NavGroup[] {
  const kinds = useRecordKinds(me.department);
  const keepsDeals = kinds.some((k) => k.entity === 'Deal');
  return (scope: Scope) => {
    const name = scope.kind === 'department' ? scope.name : me.department;
    const here = name === me.department;
    return navFor(
      me,
      {
        label: directory.label(name),
        readOnly: directory.readOnly(name),
        kinds: here ? kinds.map((k) => ({ entity: k.entity, title: entityTitle(k.entity) })) : [],
        insights: INSIGHTS.filter((i) => i.key !== 'pipeline' || (here && keepsDeals)).map((i) => ({ key: i.key, title: i.title })),
      },
      scope,
    );
  };
}

/**
 * The rail (P:33-76): the wordmark, the groups of doors, and — pinned at the
 * foot — where you are working and who you are. A person is in one
 * department at a time and every read is scoped to it, so the switcher lists
 * exactly what the session holds, named as the platform names it. The routes
 * decide what each screen shows; the rail only offers the doors, and
 * `app/nav.ts` decides which.
 */
export function NavRail({ me }: { me: Me }) {
  const frame = useFrameMaybe();
  const directory = useDepartmentNames();
  const groups = useNav(me, directory);
  const phone = frame?.layout.phone ?? false;
  const location = useLocation();
  const closeDrawer = frame?.setDrawerOpen;

  // On a phone the rail is a drawer, and going somewhere closes it.
  useEffect(() => {
    closeDrawer?.(false);
  }, [location.pathname, closeDrawer]);

  if (phone ? !frame?.drawerOpen : frame?.railHidden) return null;

  const rail = (
    <nav
      aria-label="Main"
      className={`flex h-dvh w-[var(--rail-nav)] shrink-0 flex-col gap-1 border-r border-line bg-raised px-3 pb-4 pt-2 ${
        phone ? 'relative w-[min(300px,86vw)] animate-[nrise_160ms_ease-brand_both]' : 'sticky top-0'
      }`}
    >
      <div className="mb-2 flex min-h-[52px] items-center gap-[9px] px-2.5">
        <span aria-hidden className="block h-3.5 w-3.5 bg-accent" />
        <span className="text-dense font-semibold uppercase tracking-[0.3em]">Norum</span>
        {frame && (
          <IconButton
            label="Hide menu"
            glyph={phone ? 'close' : 'collapse'}
            ghost
            onClick={() => (phone ? frame.setDrawerOpen(false) : frame.setRailHidden(true))}
            className="ml-auto"
          />
        )}
      </div>

      <div className="-mx-3 flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-3">
        {groups.map((g, n) => (
          <Group key={`${n}:${g.label ?? ''}`} {...(g.label ? { label: g.label } : {})}>
            {g.items.map((i) =>
              i.kind === 'note' ? (
                <span key={i.text} className="px-2.5 py-1 text-dense text-meta">
                  {i.text}
                </span>
              ) : i.count === 'workspace' ? (
                <WorkspaceLink key={i.to} to={i.to} label={i.label} />
              ) : i.count === 'approvals' ? (
                <ApprovalsLink key={i.to} to={i.to} label={i.label} me={me} />
              ) : i.count === 'profile_changes' ? (
                <ProfileChangesLink key={i.to} to={i.to} label={i.label} department={me.department} />
              ) : (
                <NavLink key={i.to} to={i.to} className={linkClass} {...(i.end ? { end: true } : {})}>
                  {i.label}
                </NavLink>
              ),
            )}
          </Group>
        ))}
      </div>

      <Foot me={me} directory={directory} phone={phone} />
    </nav>
  );

  if (!phone) return rail;
  // A modal sheet: focus stays inside it, Escape and a press on the scrim close it, and the page behind is hidden from a screen reader.
  return (
    <RD.Root open onOpenChange={(o) => !o && frame?.setDrawerOpen(false)}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-40 bg-[var(--scrim)]" />
        <RD.Content aria-describedby={undefined} className="fixed inset-y-0 left-0 z-50 outline-none">
          <RD.Title className="sr-only">Menu</RD.Title>
          {rail}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

/** The Workspace, with how much waits on a superadmin now (ADR-0117); proposals are not counted, as nothing is owed on them yet. */
function WorkspaceLink({ to, label }: { to: string; label: string }) {
  const queue = useWorkspaceQueue(true);
  const open = queue.data?.items.filter((i) => i.group !== 'proposal').length ?? 0;
  return (
    <NavLink to={to} className={linkClass} aria-label={open > 0 ? `${label}, ${open} waiting` : label}>
      <span className="min-w-0">{label}</span>
      <span className="ml-auto">
        <Count n={open} />
      </span>
    </NavLink>
  );
}

/** Profile changes, with how many wait for a person (S:03). */
function ProfileChangesLink({ to, label, department }: { to: string; label: string; department: string }) {
  const changes = useProfileChanges(department);
  const open = changes.data?.changes.filter((c) => c.state === 'open').length ?? 0;
  return (
    <NavLink to={to} className={linkClass} aria-label={open > 0 ? `${label}, ${open} waiting` : label}>
      <span className="min-w-0">{label}</span>
      <span className="ml-auto">
        <Count n={open} />
      </span>
    </NavLink>
  );
}

function ApprovalsLink({ to, label, me }: { to: string; label: string; me: Me }) {
  // The count is the scope's own: a department's decisions there, every one in the Control room.
  const approvals = useApprovals(useScope(me).scope.kind === 'department');
  const open = approvals.data?.items.length ?? 0;
  return (
    <NavLink to={to} className={linkClass} aria-label={open > 0 ? `${label}, ${open} waiting` : label}>
      <span className="min-w-0">{label}</span>
      <span className="ml-auto">
        <Count n={open} />
      </span>
    </NavLink>
  );
}

function Foot({ me, directory, phone }: { me: Me; directory: DepartmentNames; phone: boolean }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const { scope, options } = useScope(me);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const role = me.isSuperadmin
    ? 'Superadmin'
    : me.leads[0]
      ? `Leads ${directory.label(me.leads[0])}`
      : directory.label(me.department);

  return (
    <div ref={box} className="relative -mx-3 mt-2 flex shrink-0 flex-col gap-1.5 border-t border-line px-3 pt-2.5">
      <ScopeSwitcher me={me} directory={directory} open={open} onOpenChange={setOpen} sheet={phone} />
      {scope.kind === 'department' && directory.readOnly(scope.name) && (
        <span className="px-1 text-label text-meta">Reads only · asks the others</span>
      )}
      <div className="flex items-center gap-[9px] px-2.5 pt-2.5">
        <Avatar name={me.displayName} size={28} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-dense font-semibold">{me.displayName}</span>
          <span className="truncate text-label text-meta">{role}</span>
        </span>
        {options.length > 1 && (
          <IconButton
            label="Switch department"
            glyph="swap"
            ghost
            onClick={() => setOpen((o) => !o)}
            className="ml-auto"
          />
        )}
      </div>
    </div>
  );
}
